// eslint-disable @angular-eslint/template/interactive-supports-focus
// eslint-disable-entire-file @angular-eslint/template/click-events-have-key-events

import {
  Component,
  inject,
  ViewEncapsulation,
  PLATFORM_ID,
  TransferState,
  makeStateKey,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FullScreenLoaderComponent } from '../shared/full-screen-loader/full-screen-loader.component';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { getCookie } from '../../utils/cookies-utils';
import { NewRelicService } from '../../services/newrelic.service';

@Component({
  selector: 'al-enroll',
  imports: [CommonModule, FullScreenLoaderComponent],
  standalone: true,
  template: `
    <div class="eva-3-container -sm">
      <div>
        @if (isLoading) {
        <al-full-screen-loader></al-full-screen-loader>
        } @else {
        <div class="eva-3-container -sm -fwith">
          @if(status === LinkStatusEnum.LOGGING_INTO_APP) {
          <i
            class="eva-3-icon-cross -close-icon"
            (click)="redirectToHome($event)"
            (keydown.enter)="redirectToHome($event)"
            (keydown.space)="redirectToHome($event)"
            tabindex="0"
            role="button"
            aria-label="fechar"
          ></i>
          }
          <div class="illustration-container">
            @if (status === LinkStatusEnum.LINKED_SUCCESSFULLY || status ===
            LinkStatusEnum.ALREADY_LINKED) {
            <div class="eva-3-empty-state-message">
              <img
                class="success-image"
                src="https://www.staticontent.com/eva-core/assets/illustrations/despegar/scenes/0.0.8/help-app.svg"
                alt="success-image"
              />
            </div>
            } @else {
            <div class="eva-3-empty-state-message  -error-state">
              <div class="empty-state-message-container">
                <div class="empty-state-message-image"></div>
              </div>
            </div>
            }
          </div>

          <h3 class="eva-3-h3 title">{{ statusTitle }}</h3>
          <p class="eva-3-body-1 status-message">{{ statusMessage }}</p>
          @if(showRedirectMessage) {
          <p class="eva-3-body-2 redirect-message">
            Não sai daí, em {{ redirectTimeLeft }} segundos vamos te
            redirecionar para o iFood para você concluir.
          </p>
          }
          <button
            class="eva-3-btn -lg -primary redirect-button"
            (click)="redirect($event)"
          >
            <em class="btn-text">{{ buttonText }}</em>
          </button>
        </div>
        }
      </div>
    </div>
  `,
  styleUrls: ['./enroll.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class EnrollComponent {
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);
  private transferState = inject(TransferState);
  private newRelicService = inject(NewRelicService);

  private readonly HEADERS_KEY =
    makeStateKey<Record<string, string>>('headers_to_forward');
  protected isLoading = true;
  protected statusTitle = 'Account Linking Complete';
  protected statusMessage = 'Your account linking process is complete.';
  protected redirectMessage = 'You will be redirected shortly.';
  protected buttonText = 'Voltar ao iFood';
  protected status: LinkStatus = LinkStatus.ERROR;
  protected redirectTo =
    'ifood://webmiddleware?isImmersive=false&isModal=false&isFullscreen=false&hideTopSafeArea=true&page=https%3A%2F%2Fwebmiddleware-ecosystem.ifood.com.br%2Faccount%2Flink%3Fstatus%3Dfail%26status_detail%3DERROR';
  protected readonly playStoreRedirectTo =
    'https://play.google.com/store/apps/details?id=com.gm.decolar';
  protected readonly appStoreRedirectTo =
    'https://apps.apple.com/app/decolar-voos-e-hot%C3%A9is/id583990782';
  protected LinkStatusEnum = LinkStatus;
  protected readonly successRedirectTime = 2;
  protected readonly errorRedirectTime = 5;
  protected redirectTimeLeft = this.successRedirectTime;
  protected redirectInterval: ReturnType<typeof setInterval> | null = null;
  protected showRedirectMessage = true;
  protected isApp = false;

  protected titles = {
    LINKED_SUCCESSFULLY: 'Suas contas estão vinculadas!',
    ALREADY_LINKED: 'Suas contas estão vinculadas!',
    ALREADY_LINKED_WITH_OTHER_ID: 'Conta já vinculada',
    ALREADY_LINKED_TO_ANOTHER_USER: 'Conta já vinculada',
    LOGGING_INTO_APP: 'Login Decolar necessário',
    REDIRECT_TO_LOGIN: '',
    ERROR: 'Algo deu errado',
    APP_OUTDATED: 'App desatualizado',
  };

  protected redirectTimes = {
    LINKED_SUCCESSFULLY: this.successRedirectTime,
    ALREADY_LINKED: this.successRedirectTime,
    ALREADY_LINKED_WITH_OTHER_ID: this.errorRedirectTime,
    ALREADY_LINKED_TO_ANOTHER_USER: this.errorRedirectTime,
    LOGGING_INTO_APP: this.errorRedirectTime,
    ERROR: this.errorRedirectTime,
    APP_OUTDATED: this.errorRedirectTime,
    REDIRECT_TO_LOGIN: 0,
  };

  protected messages = {
    LINKED_SUCCESSFULLY:
      'Aproveite os benefícios do iFood e boas viagens com a Decolar!',
    ALREADY_LINKED:
      'Aproveite os benefícios do iFood e boas viagens com a Decolar!',
    ALREADY_LINKED_WITH_OTHER_ID:
      'Esta conta Decolar já está em uso com outra conta de iFood. Você pode criar uma nova conta Decolar ou usar outra existente.',
    ALREADY_LINKED_TO_ANOTHER_USER:
      'Esta conta Decolar já está em uso com outra conta de iFood. Você pode criar uma nova conta Decolar ou usar outra existente.',
    LOGGING_INTO_APP:
      'Para vincular suas contas você precisa entrar sua conta Decolar',
    REDIRECT_TO_LOGIN: '',
    ERROR:
      'Não conseguimos vincular suas contas agora. Tente novamente em alguns minutos.',
    APP_OUTDATED:
      'Seu aplicativo está desatualizado. Por favor, atualize para a versão mais recente.',
  };

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const partner = this.route.snapshot.paramMap.get('partner') as string;
      const token = this.route.snapshot.paramMap.get('token') as string;
      const action = this.route.snapshot.data['action'] as string;
      const queryString = new URLSearchParams(
        this.route.snapshot.queryParams
      ).toString();
      const headersToForward = this.transferState.get(this.HEADERS_KEY, {});
      this.linkAccount(partner, token, action, queryString, headersToForward);
    }
  }

  redirect(event?: Event) {
    this.newRelicService.addPageAction('AccountLinkerRedirect', {
      status: this.status,
      isNativeApp: this.isApp,
    });

    if (event) event.preventDefault();
    if (this.redirectInterval) clearInterval(this.redirectInterval);
    this.showRedirectMessage = false;
    window.location.href = this.redirectTo;
  }

  redirectToHome(event: Event) {
    event.preventDefault();
    if (this.redirectInterval) clearInterval(this.redirectInterval);
    this.showRedirectMessage = false;
    window.location.href = '/';
  }

  linkAccount(
    partner: string,
    token: string,
    action: string,
    queryString: string,
    headersToForward: Record<string, string>
  ) {
    this.isApp = this.isNativeApp();
    const canDoLinking = this.canProceedWithLinking(this.isApp);
    if (!canDoLinking) {
      this.status = LinkStatus.APP_OUTDATED;
      this.statusTitle = this.titles[this.status];
      this.statusMessage = this.messages[this.status];
      this.redirectTimeLeft = this.redirectTimes[this.status];
      this.isLoading = false;
      this.redirectTo =
        this.getDeviceOS() === 'iOS'
          ? this.appStoreRedirectTo
          : this.playStoreRedirectTo;
      this.buttonText =
        this.getDeviceOS() === 'iOS'
          ? 'Atualizar na App Store'
          : 'Atualizar no Google Play';
      this.createCountdown();
      return;
    }
    this.http
      .get<LinkResult>(
        `/account-linker/api/${partner}/${action}/${token}?${queryString}`,
        { headers: headersToForward }
      )
      .subscribe({
        next: (data: LinkResult) => {
          this.status = data.code;
          this.redirectTo = data.redirect_to;
          if (this.status === LinkStatus.REDIRECT_TO_LOGIN) {
            this.redirect();
            return;
          }
          this.statusTitle = this.titles[data.code];
          this.statusMessage = this.messages[data.code];
          this.isLoading = false;
          this.redirectTimeLeft = this.redirectTimes[this.status];
          this.createCountdown();
        },
        error: (response) => {
          if ((response.error as LinkResult).redirect_to) {
            this.status = (response.error as LinkResult).code;
            this.redirectTo = (response.error as LinkResult).redirect_to;
            console.error(`error linking accounts: ${this.status}`);
          } else {
            this.status = LinkStatus.ERROR;
            console.error('Unexpected error during account linking:', response);
          }
          this.statusTitle = this.titles[this.status];
          this.statusMessage = this.messages[this.status];
          this.isLoading = false;
          this.redirectTimeLeft = this.redirectTimes[this.status];
          this.createCountdown();
        },
      });
  }

  createCountdown() {
    this.redirectInterval = setInterval(() => {
      this.redirectTimeLeft--;
      if (this.redirectTimeLeft === 0) {
        this.redirect();
      }
    }, 1000);
  }

  getDeviceOS() {
    const ua = navigator.userAgent;
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    if (isAndroid) {
      return 'Android';
    }
    if (isIOS) {
      return 'iOS';
    }
    return 'unknown';
  }

  isNativeApp() {
    return !!getCookie('X-ClientInfo');
  }

  canProceedWithLinking(isNativeApp: boolean) {
    if (!isNativeApp) return true;
    const features = getCookie('xdesp-AppFeatures');
    return features && features.includes('ifoodScheme');
  }
}

enum LinkStatus {
  LINKED_SUCCESSFULLY = 'LINKED_SUCCESSFULLY',
  ALREADY_LINKED = 'ALREADY_LINKED',
  ALREADY_LINKED_WITH_OTHER_ID = 'ALREADY_LINKED_WITH_OTHER_ID',
  ALREADY_LINKED_TO_ANOTHER_USER = 'ALREADY_LINKED_TO_ANOTHER_USER',
  LOGGING_INTO_APP = 'LOGGING_INTO_APP',
  REDIRECT_TO_LOGIN = 'REDIRECT_TO_LOGIN',
  APP_OUTDATED = 'APP_OUTDATED',
  ERROR = 'ERROR',
}

interface LinkResult {
  code: LinkStatus;
  redirect_to: string;
}
